import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  debounce,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HttpService from "../../../../hooks/Https-services";
import { APIRoutes } from "../../../../utils/constants";
import { KeyboardArrowRight, KeyboardArrowLeft } from "@mui/icons-material";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import { DateRangePicker } from "rsuite";
import endOfWeek from "date-fns/endOfWeek";
import endOfYear from "date-fns/endOfYear";
import addDays from "date-fns/addDays";
import endOfMonth from "date-fns/endOfMonth";
import { startOfWeek } from "date-fns";
import useConfig from "../../../../hooks/useConfig";
import { channel } from "diagnostics_channel";
const monthList = Array.from({ length: 4 }, (_, index) => {
  const month = moment().subtract(index, "months"); // Subtract months from the current date
  return {
    id: month.format("MM"), // ID in reverse order (12, 11, ..., 1)
    value: month.format(`MMMM YYYY`), // Format to 'Month Year'
    year: month.format(`YYYY`),
  };
});
export interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

export function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const ViewReport = () => {
  const [month, setMonth] = React.useState(moment().format("MM"));
  const [channelOption, setChannelOption] = React.useState<any>([]);
  const [eventsOption, setEventsOption] = React.useState<any>([]);
  const [gameIds, setGameIds] = useState([]);
  const [teamOption, setTeamOption] = React.useState<any>([]);
  const [eventOption, setEventOption] = React.useState<any>([
    { label: "All", value: "" },
    { label: "Live", value: "live" },
    { label: "VOD", value: "vod" },
  ]);
  const configData = useConfig();
  const [filters, setFilters] = React.useState<any>({
    start_date: new Date(moment().startOf("month").format("YYYY-MM-DD")),
    end_date: new Date(moment().endOf("month").format("YYYY-MM-DD")),
    channel: [],
    team: [],
    event: [],
  });
  const predefinedRanges: any = [
    {
      label: "Today",
      value: [new Date(), new Date()],
      placement: "left",
    },

    {
      label: "This week",
      value: [
        new Date(moment().startOf("week").format("YYYY-MM-DD")),
        new Date(moment().endOf("week").format("YYYY-MM-DD")),
      ],
      placement: "left",
    },

    {
      label: "This month",
      value: [
        new Date(moment().startOf("month").format("YYYY-MM-DD")),
        endOfMonth(new Date()),
      ],
      placement: "left",
    },

    {
      label: "This year",
      value: [
        new Date(moment().startOf("year").format("YYYY-MM-DD")),
        endOfYear(new Date()),
      ],
      placement: "left",
    },

    {
      label: "Next week",
      closeOverlay: false,
      value: (value: any) => {
        const [start = new Date()] = value || [];
        return [
          addDays(startOfWeek(start, { weekStartsOn: 0 }), 7),
          addDays(endOfWeek(start, { weekStartsOn: 0 }), 7),
        ];
      },
      appearance: "default",
    },
  ];
  const [allReports, setAllReports] = React.useState({} as any);
  const [keyword, setKeyword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const navigate = useNavigate();
  // Avoid a layout jump when reaching the last page with empty rows.
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - allReports?.total) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const GetGames = useCallback(async () => {
    try {
      const firstDateOfMonth = moment(filters?.start_date)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      const lastDateOfMonth = moment(filters?.end_date)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");

      const params: any = {
        start: firstDateOfMonth,
        end: lastDateOfMonth,
        page: 1,
        limit: 2000,
        channel: filters?.channel,
        customer: configData.customer,
      };
      const games = await HttpService.getapi(
        `${APIRoutes.TeamEventsList}?` + new URLSearchParams(params)
      );
      const gameID = games?.data.data?.map((el: any) => el.game_id).join(",");
      setGameIds(gameID);
    } catch (error) {
      console.log(error);
    }
  }, [filters.channel, filters.start_date, filters.end_date]);
  const GetAllReports = React.useCallback(async () => {
    try {
      if (!gameIds?.length) {
        return;
      }
      setIsLoading(true);
      const query: any = {
        start_date: moment(filters?.start_date).format("YYYY-MM-DD HH:mm:ss"),
        end_date: moment(filters?.end_date).format("YYYY-MM-DD HH:mm:ss"),
        channel: filters?.channel,
        team_id: filters?.team?.map((item: any) => item?.value),
        event_id: filters?.event,
        page: page + 1,
        limit: rowsPerPage,
        game_id: gameIds,
      };
      const res = await HttpService.get(
        `${APIRoutes.GetViewReport}?${new URLSearchParams(query).toString()}`
      );
      setAllReports(res?.data?.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [month, page, rowsPerPage, filters, gameIds]);
  const handleKeywordChange = debounce((value: string) => {
    setKeyword(value);
  }, 300); //
  React.useEffect(() => {
    GetAllReports();
  }, [GetAllReports]);
  useEffect(() => {
    GetGames();
  }, [GetGames]);
  const DownloadAllReports = async () => {
    if (!gameIds?.length) {
      return;
    }
    try {
      setIsDownloading(true);

      const findYear = monthList.find((ele: any) => ele.id == month);
      const query: any = {
        start_date: moment(filters?.start_date).format("YYYY-MM-DD HH:mm:ss"),
        end_date: moment(filters?.end_date).format("YYYY-MM-DD HH:mm:ss"),
        channel: filters?.channel,
        team_id: filters?.team?.map((item: any) => item?.value),
        event_id: filters?.event,
        page: page + 1,
        limit: rowsPerPage,
        game_id: gameIds,
      };
      const res = await HttpService.get(
        `${APIRoutes.GetViewReport}?${new URLSearchParams(
          query
        ).toString()}&month=${month}&isDownload=true&year=${findYear?.year}`
      );
      downloadJSONAsCSV(res?.data?.data?.rows);
      setIsDownloading(false);
    } catch (error) {
      setIsDownloading(false);
    }
  };
  function jsonToCSV(jsonData: any) {
    // Get the headers
    const headers = Object.keys(jsonData[0]).join(",") + "\n";

    // Get the rows
    const rows = jsonData
      .map((row: any) =>
        Object.values({
          game_id: row?.GameID,
          date: row?.game_date_time?.value, // Access the nested value
          away_team: row?.away_team,
          home_team: row?.home_team,
          live: row?.live,
          liveAway: row?.liveAway,
          vod: row?.vod,
          vodAway: row?.vodAway,
          minutesWatched: row?.minutesWatched,
          minutesWatchedAway: row?.minutesWatchedAway,
        }).join(",")
      )
      .join("\n");

    return headers + rows;
  }
  function downloadJSONAsCSV(jsonData: any, filename = "reports.csv") {
    // Convert JSON to CSV
    const csv = jsonToCSV(jsonData);

    // Create a Blob from the CSV string
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // Create a link to download the Blob
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  const FetchChannels = async () => {
    try {
      const result = await HttpService.getapi(
        APIRoutes.ChannelOption.replace(":id", configData.customer)
      );
      const channelOption = result?.data?.data.map((channel: any) => ({
        label: channel?.channel_name,
        value: channel?.channel_id,
      }));
      setChannelOption(channelOption);
    } catch (error) {
      console.log(error);
    }
  };

  const FetchEvents = useCallback(async () => {
    try {
      const query = {
        customer_id: configData.customer,
        client_ids: filters?.channel,
      };
      const result = await HttpService.getapi(
        APIRoutes.GetEventsAdmin + "?" + new URLSearchParams(query).toString()
      );
      const events = result?.data?.data.map((channel: any) => ({
        label: channel?.event_name,
        value: channel?.event_id,
      }));
      setEventOption(events);
    } catch (error) {
      console.log(error);
    }
  }, [filters?.channel]);
  React.useEffect(() => {
    FetchChannels();
  }, []);
  const FetchTeams = useCallback(async () => {
    try {
      const query = {
        channelIDs: filters?.channel,
      };
      const result = await HttpService.getapi(
        APIRoutes.GetTeamsAdmin.replace(":customer", configData.customer) +
          "?" +
          new URLSearchParams(query).toString()
      );
      const teams = result?.data?.data.map((channel: any) => ({
        label: `${channel?.team_name} ${channel?.team_mascot}`,
        value: channel?.team_id,
      }));
      setTeamOption(teams);
    } catch (error) {
      console.log(error);
    }
  }, [filters?.channel]);

  useEffect(() => {
    FetchEvents();
  }, [FetchEvents]);
  useEffect(() => {
    FetchTeams();
  }, [FetchTeams]);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", rowGap: 0 }}>
          <Box sx={{ minWidth: "300px" }}>
            <Typography>Date</Typography>
            <DateRangePicker
              className="datepicker"
              // size="lg"
              block
              value={[filters?.start_date, filters?.end_date]}
              showOneCalendar
              style={{ width: "100%" }}
              ranges={predefinedRanges}
              onChange={(value: any) => {
                setFilters({
                  ...filters,
                  start_date: value?.[0],
                  end_date: value?.[1],
                });
              }}
              placeholder="Select date range"
            />
          </Box>
          <Box>
            <Typography>Channel</Typography>
            <Select
              sx={{ minWidth: "200px" }}
              value={filters?.channel?.length > 0 ? filters?.channel : [""]}
              onChange={(e: any) => {
                setFilters({
                  ...filters,
                  channel: e.target.value.filter((item: any) => item !== ""),
                  event: [],
                });
              }}
              multiple
              size="small"
              displayEmpty
              disabled={isLoading}
            >
              <MenuItem value="">All</MenuItem>
              {channelOption?.map((channel: any) => (
                <MenuItem key={channel?.value} value={channel?.value}>
                  {channel?.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography>Event</Typography>
            <Select
              sx={{ maxWidth: "200px", minWidth: "200px" }}
              value={filters?.event?.length > 0 ? filters?.event : [""]}
              multiple
              onChange={(e: any) => {
                setFilters({
                  ...filters,
                  event: e.target.value.filter((item: any) => item !== ""),
                });
              }}
              size="small"
              displayEmpty
              disabled={isLoading}
            >
              <MenuItem value="">All</MenuItem>
              {eventOption?.map((event: any) => (
                <MenuItem key={event?.value} value={event?.value}>
                  {event?.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography>Team</Typography>
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              getOptionLabel={(option: any) => {
                return option?.label;
              }}
              options={teamOption}
              multiple
              value={filters?.team?.length > 0 ? filters?.team : []}
              groupBy={(option: any) => option?.firstLetter}
              onChange={(event: any, value: any) => {
                setFilters({
                  ...filters,
                  team: value?.map((item: any) => item),
                });
              }}
              size="small"
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option?.team_id}>
                  <Typography
                    variant="body1"
                    sx={{ fontStyle: "normal", textTransform: "none" }}
                  >
                    {" "}
                    {option?.label}
                  </Typography>
                </Box>
              )}
              sx={{
                width: 1,
                maxWidth: "200px",
                minWidth: "200px",
                fieldset: {
                  borderColor: "#959799",
                },
              }}
              renderGroup={(params) => (
                <li key={params.key}>
                  <>{params.children}</>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    borderColor: "#959799",
                    input: { height: "25px" },
                  }}
                />
              )}
              className=""
            />
          </Box>
          <Box>
            <Button
              sx={{ mt: 3 }}
              variant="outlined"
              onClick={() =>
                setFilters({
                  start_date: new Date(
                    moment().startOf("month").format("YYYY-MM-DD")
                  ),
                  end_date: new Date(
                    moment().endOf("month").format("YYYY-MM-DD")
                  ),
                  channel: [],
                  event: [],
                  team: [],
                })
              }
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
        <Box>
          <Box sx={{ maxHeight: "40px", mt: 2 }}>
            <LoadingButton
              onClick={DownloadAllReports}
              loading={isDownloading}
              size="large"
              sx={{ whiteSpace: "nowrap" }}
              variant="contained"
            >
              Download CSV
            </LoadingButton>
          </Box>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Game ID
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Game Date
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Away Team
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Home Team
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Live
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Live Away
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Vod
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Vod Away
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Total Minute Watched
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "normal",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Total Minute Watched Away
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableBody>
              {new Array(10).fill(0).map((el) => {
                return (
                  <TableRow>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant={"rounded"} sx={{ p: 2 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          ) : (
            <TableBody>
              {allReports?.rows?.map((row: any) => (
                <TableRow key={row.GameID}>
                  <TableCell component="th" scope="row">
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "normal", textTransform: "none" }}
                    >
                      {row.GameID}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "normal", textTransform: "none" }}
                    >
                      {moment(row?.game_date_time?.value).format(
                        "DD MMM YYYY hh:mm A"
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "normal", textTransform: "none" }}
                    >
                      {row?.away_team}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "normal", textTransform: "none" }}
                    >
                      {row?.home_team}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="center">
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "normal", textTransform: "none" }}
                    >
                      {row?.live}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.liveAway}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.vod}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.vodAway}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.minutesWatched?.toFixed(2) || 0}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: 160 }} align="left">
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.minutesWatchedAway?.toFixed(2) || 0}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        colSpan={3}
        rowsPerPageOptions={[10, 20, 30]}
        count={allReports.total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </Box>
  );
};
export default ViewReport;
